import React from 'react'
import loadable from '@loadable/component'
import { HeroSection } from '@/components/organisms/HeroSection/HeroSection'
import { HeroAbout } from '@/components/organisms/HeroAbout/HeroAbout'
import { PricingSection } from '@/components/organisms/PricingSection/PricingSection'
import { FeaturesTableSection } from '@/components/organisms/FeaturesTableSection/FeaturesTableSection'
import { FeatureListSection } from '@/components/organisms/FeatureListSection/FeatureListSection'
import { AdvantagesGrid } from '@/components/organisms/AdvantagesGrid/AdvantagesGrid'
import { AdvantagesSection } from '@/components/organisms/AdvantagesSection/AdvantagesSection'
import { AdvantagesLinks } from '@/components/organisms/AdvantagesLinks/AdvantagesLinks'
import { TeamSection } from '@/components/organisms/TeamSection/TeamSection'
import { MarkdownContent } from '@/components/organisms/MarkdownContent/MarkdownContent'
import { PageNotFound } from '@/components/organisms/PageNotFound/PageNotFound'
import { CollectionSection } from '@/components/organisms/CollectionSection/CollectionSection'
import { QuotesSection } from '@/components/organisms/QuotesSection/QuotesSection'
import { LandingPageSection } from '@/components/organisms/LandingPageSection/LandingPageSection'
import { CalendlyCode } from '@/components/organisms/CalendlyCode/CalendlyCode'
import { DiscoverySection } from '@/components/organisms/DiscoverySection/DiscoverySection'
import { HelpSearchResultsSection } from '@/components/organisms/HelpSearchResultSection/HelpSearchResultSection'
import { ImageWithText } from '@/components/organisms/ImageWithText/ImageWithText'
import { EventAnnouncer } from '@/components/organisms/EventAnnouncer/EventAnnouncer'
import { EventSectionCenter } from '@/components/organisms/EventSectionCenter/EventSectionCenter'
import { HeroWebinar } from '@/components/organisms/HeroWebinar/HeroWebinar'
import { GatedContentSection } from '@/components/organisms/GatedContentSection/GatedContentSection'
import { VideoLibrarySection } from '@/components/organisms/VideoLibrarySection/VideoLibrarySection'
import { HeroIntegrations } from '@/components/organisms/HeroIntegrations/HeroIntegrations'
import { IntegrationsGrid } from '@/components/organisms/IntegrationsGrid/IntegrationsGrid'
import { CTAIntegrationBannerSection } from '@/components/organisms/CTAIntegrationBannerSection/CTAIntegrationBannerSection'
import { HeroSingleCaseStudy } from '@/components/organisms/HeroSingleCaseStudy/HeroSingleCaseStudy'
import { CaseStudyContent } from '@/components/organisms/CaseStudyContent/CaseStudyContent'
import { SectionCustomerStories } from '@/components/organisms/SectionCustomerStories/SectionCustomerStories'
import { NumbersSection } from '@/components/organisms/NumbersSection/NumbersSection'
import { HeroCaseStudies } from '@/components/organisms/HeroCaseStudies/HeroCaseStudies'
import { SectionCaseStudiesGrid } from '@/components/organisms/SectionCaseStudiesGrid/SectionCaseStudiesGrid'
import { SectionSalesCall } from '@/components/organisms/SectionSalesCall/SectionSalesCall'
import { SectionConfirmation } from '@/components/organisms/SectionConfirmation/SectionConfirmation'

const SearchResultsSection = loadable(
  () => import('@/components/organisms/SearchResultsSection/SearchResultsSection'),
  {
    resolveComponent: (component) => component.SearchResultsSection
  }
)

const HappyClientsSection = loadable(() => import('@/components/organisms/HappyClientsSection/HappyClientsSection'), {
  resolveComponent: (component) => component.HappyClientsSection
})
const TrustedBySection = loadable(() => import('@/components/organisms/TrustedBySection/TrustedBySection'), {
  resolveComponent: (component) => component.TrustedBySection
})
const CTASection = loadable(() => import('@/components/organisms/CTASection/CTASection'), {
  resolveComponent: (component) => component.CTASection
})
const CTASectionIntegrations = loadable(
  () => import('@/components/organisms/CTASectionIntegrations/CTASectionIntegrations'),
  {
    resolveComponent: (component) => component.CTASectionIntegrations
  }
)
const AwardsSection = loadable(() => import('@/components/organisms/AwardsSection/AwardsSection'), {
  resolveComponent: (component) => component.AwardsSection
})
const FAQ = loadable(() => import('@/components/organisms/FAQ/FAQ'), {
  resolveComponent: (component) => component.FAQ
})
const FeaturesSection = loadable(() => import('@/components/organisms/FeaturesSection/FeaturesSection'), {
  resolveComponent: (component) => component.FeaturesSection
})
const ReviewsSection = loadable(() => import('@/components/organisms/ReviewsSection/ReviewsSection'), {
  resolveComponent: (component) => component.ReviewsSection
})
const MarketplaceRatingsSection = loadable(
  () => import('@/components/organisms/MarketplaceRatingsSection/MarketplaceRatingsSection'),
  {
    resolveComponent: (component) => component.MarketplaceRatingsSection
  }
)
const CustomerStoriesPromoBannerSection = loadable(
  () => import('@/components/organisms/CustomerStoriesPromoBanner/CustomerStoriesPromoBanner'),
  {
    resolveComponent: (component) => component.CustomerStoriesPromoBanner
  }
)

const Components = {
  section_hero: HeroSection,
  section_hero_about: HeroAbout,
  section_awards: AwardsSection,
  section_faq: FAQ,
  section_pricing: PricingSection,
  section_features_table: FeaturesTableSection,
  section_feature_list: FeatureListSection,
  section_advantages: AdvantagesSection,
  section_advantages_grid: AdvantagesGrid,
  section_advantages_links: AdvantagesLinks,
  section_happy_clients: HappyClientsSection,
  section_trusted_by: TrustedBySection,
  section_cta: CTASection,
  section_team: TeamSection,
  section_markdown_content: MarkdownContent,
  section_404: PageNotFound,
  section_collection: CollectionSection,
  section_error_page: PageNotFound,
  section_quotes: QuotesSection,
  section_landing_page: LandingPageSection,
  section_calendly_code: CalendlyCode,
  section_search_results: SearchResultsSection,
  section_discovery: DiscoverySection,
  section_help_search_results: HelpSearchResultsSection,
  section_image_text: ImageWithText,
  section_who_is_it_for: FeatureListSection,
  section_event_announcer: EventAnnouncer,
  section_event_center: EventSectionCenter,
  section_hero_webinar: HeroWebinar,
  section_gated_content: GatedContentSection,
  section_video_library: VideoLibrarySection,
  section_features: FeaturesSection,
  section_reviews: ReviewsSection,
  section_marketplace_ratings: MarketplaceRatingsSection,
  section_hero_integrations: HeroIntegrations,
  section_cta_integrations: CTASectionIntegrations,
  section_integrations_grid: IntegrationsGrid,
  section_cta_integrations_banner: CTAIntegrationBannerSection,
  section_hero_single_case_study: HeroSingleCaseStudy,
  section_case_study: CaseStudyContent,
  section_customer_stories: SectionCustomerStories,
  section_numbers: NumbersSection,
  section_hero_case_studies: HeroCaseStudies,
  section_case_studies_grid: SectionCaseStudiesGrid,
  section_sales_call: SectionSalesCall,
  section_confirmation: SectionConfirmation,
  section_cs_promo_banner: CustomerStoriesPromoBannerSection
}

export function DynamicComponent({ blok, location }: { blok: any; location: Location }) {
  // @ts-ignore
  if (typeof Components[blok.component] !== 'undefined') {
    // @ts-ignore
    const Component = Components[blok.component]
    return <Component {...blok} key={blok._uid} location={location} />
  }

  return blok.component || <p>Not found: {blok.component}</p>
}

export default DynamicComponent
